import { useEffect, useMemo, useRef } from 'react';
import { AllMapLayers, MapLayer } from 'components/Map/constants';
import { useExternalFeatureLayers } from 'features/mapping/hooks/useExternalFeatureLayers';
import { EXTERNAL_FEATURE_MAP_LAYER } from 'features/wildfires/wildfire.constants';
import { LayerConfig } from 'features/wildfires/wildfire.types';
import { useMapLayersState } from 'state/useMapLayersState';

const isExternalLayer = (layer: MapLayer): boolean =>
  layer === EXTERNAL_FEATURE_MAP_LAYER;

export const useUpdateLayersConfig = (config: LayerConfig): void => {
  const { disable, enable, turnOff, turnOn } = config;
  const { mapLayers, setMapLayerVisibility, setDisabledLayers } =
    useMapLayersState();
  const { externalFeatureLayers, isLoading: isLoadingExternalFeatureLayers } =
    useExternalFeatureLayers();

  const initialActiveMapLayersRef = useRef(
    new Map(mapLayers.map((layer) => [layer, true])),
  );

  const externalFeatureLayerIds = useMemo(
    () => externalFeatureLayers.map((layer) => layer.mapLayerId),
    [externalFeatureLayers],
  );

  const layersToTurnOff = useMemo(() => {
    return turnOff.flatMap((layer) => {
      if (isExternalLayer(layer)) {
        return externalFeatureLayerIds.filter((layerName) =>
          initialActiveMapLayersRef.current.has(layerName),
        );
      }
      return initialActiveMapLayersRef.current.has(layer) ? layer : [];
    });
  }, [externalFeatureLayerIds, turnOff]);

  const layersToTurnOn = useMemo(() => {
    return turnOn.flatMap((layer) => {
      if (isExternalLayer(layer)) {
        return externalFeatureLayerIds.filter(
          (layerName) => !initialActiveMapLayersRef.current.has(layerName),
        );
      }
      return initialActiveMapLayersRef.current.has(layer) ? [] : layer;
    });
  }, [externalFeatureLayerIds, turnOn]);

  const layersToDisable = useMemo(() => {
    const baseLayers = AllMapLayers.filter(
      (layer) => disable.includes(layer) && !enable.includes(layer),
    );

    const externalLayers =
      disable.includes(EXTERNAL_FEATURE_MAP_LAYER) &&
      !enable.includes(EXTERNAL_FEATURE_MAP_LAYER)
        ? externalFeatureLayerIds
        : [];

    return [...baseLayers, ...externalLayers];
  }, [disable, enable, externalFeatureLayerIds]);

  const hasTurnedOffRef = useRef(false);
  useEffect(() => {
    if (isLoadingExternalFeatureLayers || hasTurnedOffRef.current) return;
    hasTurnedOffRef.current = true;
    layersToTurnOff.forEach((layer) => setMapLayerVisibility(layer, false));
  }, [isLoadingExternalFeatureLayers, layersToTurnOff, setMapLayerVisibility]);

  const hasTurnedOnRef = useRef(false);
  useEffect(() => {
    if (isLoadingExternalFeatureLayers || hasTurnedOnRef.current) return;
    hasTurnedOnRef.current = true;
    layersToTurnOn.forEach((layer) => setMapLayerVisibility(layer, true));
  }, [isLoadingExternalFeatureLayers, layersToTurnOn, setMapLayerVisibility]);

  const hasDisabledRef = useRef(false);
  useEffect(() => {
    if (isLoadingExternalFeatureLayers || hasDisabledRef.current) return;
    hasDisabledRef.current = true;
    setDisabledLayers(layersToDisable);
  }, [isLoadingExternalFeatureLayers, layersToDisable, setDisabledLayers]);
};
